import BigNumber from 'bignumber.js'
import { BaseAccountController, TAccountControllerConfig } from './BaseAccountController'
import { cacheTokens, getCachedTokens } from '../../utils/tokenCache'
import {
  base64ToBytes,
  fetchSuiWalletTokens,
  getSuiCoinTypeByTarget,
  getSuiQuote,
  getSuiSwapTransaction,
  getSuiTokenInfo,
  getSuiTokenPrices,
  SUI_COIN_TYPE,
} from '../../coinLiquidation/suiUtils'
import {
  BuyBackItem,
  LiquidationAsset,
  NetworkConfig,
  TargetCoinLiquidation,
} from '../../coinLiquidation/types'
import { addBuyBackItem, updateBuyBackItemTxHash } from '../../coinLiquidation/buybackUtils'
import { DEFAULT_ALLOWED_SLIPPAGE } from '../../coinLiquidation/constants'
import { WalletTokenState } from '../../coinLiquidation/walletTokenFetcher'
import { Transaction } from '@mysten/sui/transactions'
import { SuiClient } from '@mysten/sui/client'

export class SuiAccountController implements BaseAccountController {
  accountState: WalletTokenState | null
  private address: string
  private _config: TAccountControllerConfig
  private _connection = new SuiClient({
    url: 'https://autumn-wiser-valley.sui-mainnet.quiknode.pro/fe51bf978474f207048c8a11ca213e1a3a3e9c8d/',
  })

  constructor(_address: string, config: TAccountControllerConfig) {
    this.address = _address
    this.loadCache = this.loadCache.bind(this)
    this.loadCache()
    this._config = config
  }

  updateConfig(config: TAccountControllerConfig): void {
    this._config = config
  }

  loadCache() {
    const cached = getCachedTokens(this.address)
    if (cached) {
      this.accountState = cached
    }
    return cached
  }

  async loadAccountTokens(address: string, callback: (progress: any) => void) {
    const walletState = await fetchSuiWalletTokens(
      address,
      this._config.suiRpcUrl,
      this._config.suiSwapApiUrl,
      callback,
    )
    this.accountState = walletState
    if (walletState) {
      cacheTokens(address, walletState)
    }
    return this.accountState
  }

  async getSourceAmountByLiquidationType({
    inputToken,
  }: {
    inputToken: LiquidationAsset
    network: NetworkConfig
  }) {
    console.log('inputToken', inputToken)
    const balance = inputToken.balanceString || '0'
    const normalized = new BigNumber(balance || 0)
    if (normalized.isNaN() || normalized.lte(0)) {
      this._config.onHandleError(
        `${inputToken?.network?.chainId}.${inputToken.symbol}`,
        'insufficientAmount',
      )
      return
    }
    return normalized.toString()
  }

  private async signAndExecuteSuiTransaction(txInput: string | Uint8Array | Transaction) {
    const wallet = this._config.suiWallet
    if (!wallet) {
      throw new Error('Sui wallet not connected')
    }
    console.log('wallet', wallet)
    const transaction =
      typeof (txInput as any)?.toJSON === 'function' ||
      typeof (txInput as any)?.serialize === 'function'
        ? (txInput as Transaction)
        : Transaction.from(
            typeof txInput === 'string' && txInput.trim().startsWith('{')
              ? txInput
              : typeof txInput === 'string'
                ? base64ToBytes(txInput)
                : txInput,
          )
    const account = wallet.accounts?.[0]
    if (!account) {
      throw new Error('Sui wallet account not available')
    }
    if (typeof (transaction as any)?.setSenderIfNotSet === 'function') {
      transaction.setSenderIfNotSet(account.address)
    }
    const chain =
      account?.chains?.[0] ||
      (this._config.suiRpcUrl?.includes('testnet')
        ? 'sui:testnet'
        : this._config.suiRpcUrl?.includes('devnet')
          ? 'sui:devnet'
          : 'sui:mainnet')
    if (wallet.signAndExecuteTransaction) {
      return wallet.signAndExecuteTransaction({
        transaction,
        account,
        chain,
      })
    }
    if (wallet.signAndExecuteTransactionBlock) {
      return wallet.signAndExecuteTransactionBlock({
        transactionBlock: transaction,
        account,
        chain,
      })
    }
    throw new Error('Sui wallet does not support transaction signing')
  }

  async onSwapToken({
    sourceToken,
    network,
  }: {
    sourceToken: LiquidationAsset
    network: NetworkConfig
  }) {
    try {
      console.log('this._config', this._config)
      const outputCoinType = await getSuiCoinTypeByTarget(
        this._config.targetCoin,
        this._config.suiSwapApiUrl,
        this._config.suiRpcUrl,
      )
      if (!outputCoinType) {
        this._config.onHandleError(
          `${network.chainId}.${sourceToken.symbol}`,
          'unsupportedOutputToken',
        )
        return
      }

      const sourceAmount = await this.getSourceAmountByLiquidationType({
        inputToken: sourceToken,
        network,
      })
      if (!sourceAmount) {
        return
      }

      const amountInBaseUnits = new BigNumber(sourceAmount)
        .multipliedBy(10 ** sourceToken.decimals)
        .toFixed(0)

      console.log('amountInBaseUnits', amountInBaseUnits)
      if (!amountInBaseUnits || amountInBaseUnits === '0') {
        this._config.onHandleError(`${network.chainId}.${sourceToken.symbol}`, 'insufficientAmount')
        return
      }

      const slippageBps = Math.floor(DEFAULT_ALLOWED_SLIPPAGE * 10000)
      const quote = await getSuiQuote({
        inputCoinType: sourceToken.address,
        outputCoinType,
        amount: amountInBaseUnits,
        slippageBps,
        swapApiUrl: this._config.suiSwapApiUrl,
        rpcUrl: this._config.suiRpcUrl,
      })

      console.log('quote', quote)
      if (!quote) {
        return
      }

      const swapResponse = await getSuiSwapTransaction({
        quote,
        userAddress: this._config.account,
        swapApiUrl: this._config.suiSwapApiUrl,
        rpcUrl: this._config.suiRpcUrl,
        slippageBps,
      })

      let txInput: string | Uint8Array | Transaction | null =
        swapResponse &&
        (typeof (swapResponse as any)?.toJSON === 'function' ||
          typeof (swapResponse as any)?.serialize === 'function')
          ? (swapResponse as Transaction)
          : null

      if (txInput && txInput instanceof Transaction) {
        txInput.setSenderIfNotSet(this._config.account)
      }

      if (!txInput) {
        txInput =
          (swapResponse as any)?.txBytes ||
          (swapResponse as any)?.transactionBlockBytes ||
          (swapResponse as any)?.transactionBlock ||
          (swapResponse as any)?.bytes ||
          null
      }

      if (!txInput) {
        throw new Error('Sui swap response missing transaction payload')
      }

      const execution = await this.signAndExecuteSuiTransaction(txInput)
      const txHash =
        execution?.digest ||
        execution?.transactionDigest ||
        execution?.effects?.transactionDigest ||
        ''

      const outputTokenMeta = await getSuiTokenInfo(
        outputCoinType,
        this._config.suiRpcUrl,
        this._config.suiSwapApiUrl,
      )
      const outputToken = {
        symbol: outputTokenMeta.symbol || sourceToken.symbol,
        name: outputTokenMeta.name || sourceToken.name,
        address: outputCoinType,
        decimals: outputTokenMeta.decimals || sourceToken.decimals,
        isNative:
          outputCoinType === SUI_COIN_TYPE ||
          this._config.targetCoin === TargetCoinLiquidation.NATIVE,
      }

      const outAmountRaw =
        quote?.amountOut || swapResponse?.amountOut || swapResponse?.outputAmount || '0'
      const destAmount = new BigNumber(outAmountRaw).dividedBy(10 ** outputToken.decimals).toFixed()

      const prices = await getSuiTokenPrices(
        [sourceToken.address, outputCoinType],
        this._config.suiSwapApiUrl,
        this._config.suiRpcUrl,
      )
      const inputPrice = prices[sourceToken.address.toLowerCase()] || 0
      const outputPrice = prices[outputCoinType.toLowerCase()] || 0

      return {
        success: true,
        txHash,
        inputToken: sourceToken,
        outputToken,
        network,
        sourceAmount,
        destAmount,
        srcUSD: Number(sourceAmount) * inputPrice,
        destUSD: Number(destAmount) * outputPrice,
      }
    } catch (error: any) {
      console.log('on swap token error', error)
      this._config.onHandleError(
        `${network.chainId}.${sourceToken.symbol}`,
        error?.message || error,
      )
    }
  }

  async sellAll(tokens: Array<LiquidationAsset>, callback: (info: any) => void) {
    console.log('sellALl', tokens)
    for (const token of tokens) {
      let info: any = {}
      try {
        const response = await this.onSwapToken({
          sourceToken: token,
          network: token.network,
        })
        info.response = response

        if (response?.success && this._config.account) {
          const {
            inputToken,
            outputToken,
            network,
            sourceAmount,
            destAmount,
            srcUSD,
            destUSD,
            txHash,
          } = response

          const buybackItem = addBuyBackItem(this._config.account, {
            inputToken: {
              symbol: inputToken.symbol || token.symbol,
              name: inputToken.name || token.name,
              address: inputToken.address || token.address,
              decimals: inputToken.decimals || token.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative: token.isNative || false,
            },
            outputToken: {
              symbol: outputToken.symbol || token.symbol,
              name: outputToken.name || token.name,
              address: outputToken.address || token.address,
              decimals: outputToken.decimals || token.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative: outputToken.isNative || false,
            },
            soldAmountUSD: srcUSD,
            receivedAmountUSD: destUSD,
            buybackAmountUSD: srcUSD,
            soldAmount: sourceAmount,
            receivedAmount: destAmount,
            txHash,
          })
          info.buybackItem = buybackItem
        }
      } catch (_) {}
      info.loadingMessage = `${token.network.chainId}.${token.symbol}`
      callback(info)
    }
  }

  async buyBackItem(item: BuyBackItem, ssCb: (swapHash: string) => void, errCb: () => void) {
    if (!this.address) return
    try {
      const inputCoinType = item.outputToken.address
      const outputCoinType = item.inputToken.address

      const amountInBaseUnits = new BigNumber(item.receivedAmount)
        .multipliedBy(10 ** item.outputToken.decimals)
        .integerValue(BigNumber.ROUND_FLOOR)
        .toFixed(0)

      const slippageBps = Math.floor(DEFAULT_ALLOWED_SLIPPAGE * 10000)
      const quote = await getSuiQuote({
        inputCoinType,
        outputCoinType,
        amount: amountInBaseUnits,
        slippageBps,
        swapApiUrl: this._config.suiSwapApiUrl,
        rpcUrl: this._config.suiRpcUrl,
      })

      const swapResponse = await getSuiSwapTransaction({
        quote,
        userAddress: this._config.account,
        swapApiUrl: this._config.suiSwapApiUrl,
        rpcUrl: this._config.suiRpcUrl,
        slippageBps,
      })

      let txInput: string | Uint8Array | Transaction | null =
        swapResponse &&
        (typeof (swapResponse as any)?.toJSON === 'function' ||
          typeof (swapResponse as any)?.serialize === 'function')
          ? (swapResponse as Transaction)
          : null

      if (txInput && txInput instanceof Transaction) {
        txInput.setSenderIfNotSet(this._config.account)
      }

      if (!txInput) {
        txInput =
          (swapResponse as any)?.txBytes ||
          (swapResponse as any)?.transactionBlockBytes ||
          (swapResponse as any)?.transactionBlock ||
          (swapResponse as any)?.bytes ||
          null
      }
      if (!txInput) {
        throw new Error('Sui swap response missing transaction payload')
      }
      const execution = await this.signAndExecuteSuiTransaction(txInput)
      const txHash =
        execution?.digest ||
        execution?.transactionDigest ||
        execution?.effects?.transactionDigest ||
        ''

      updateBuyBackItemTxHash(this._config.account, item.id, txHash)
      ssCb(txHash)
    } catch (error) {
      errCb()
    }
  }
}
